import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO, { siteJsonLd, softwareJsonLd } from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Wand2, Image as ImageIcon, Zap, Download, Video, Sparkles, ArrowRight, Play, PenLine, Settings2 } from "lucide-react";

const HERO_BG = "https://images.pexels.com/photos/18419510/pexels-photo-18419510.jpeg?auto=compress&cs=tinysrgb&w=1600";
const FEATURE_IMG = "https://images.pexels.com/photos/28122495/pexels-photo-28122495.jpeg?auto=compress&cs=tinysrgb&w=1200";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function Landing() {
  const reduce = useReducedMotion();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO
        title="VisionAI Studio — Générez des vidéos IA en quelques secondes"
        description="Transformez vos idées en vidéos IA cinématographiques à partir de texte ou d'images. 3 crédits offerts. Génération HD ultra-rapide avec Kling 2.1."
        path="/"
        jsonLd={softwareJsonLd}
      />
      <Navbar />

      {/* HERO */}
      <main id="main-content">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        {/* Floating orbs */}
        <motion.div
          className="absolute top-40 -left-20 w-96 h-96 rounded-full bg-indigo-600/20 blur-3xl"
          animate={reduce ? {} : { y: [0, 30, 0], x: [0, 10, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-20 right-0 w-80 h-80 rounded-full bg-cyan-500/15 blur-3xl"
          animate={reduce ? {} : { y: [0, -20, 0], x: [0, -15, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 opacity-25 overflow-hidden">
          <img
            src={HERO_BG}
            alt=""
            fetchpriority="high"
            decoding="async"
            width="1600"
            height="900"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/60 to-[#050505]" />

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32">
          <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-3xl">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/70">Nouveau · Kling 2.1</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-bold leading-[1.05]">
              Transformez vos idées en{" "}
              <span className="text-gradient">vidéos IA</span> en quelques secondes.
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 text-lg text-white/60 max-w-xl leading-relaxed">
              Créez des vidéos cinématographiques à partir de simples textes ou images. Sans compétences techniques.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-indigo-600 hover:bg-indigo-500 btn-glow font-medium">
                Commencer gratuitement <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/pricing" data-testid="hero-pricing" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 hover:bg-white/5 font-medium">
                Voir les tarifs
              </Link>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-6 text-xs text-white/40 font-mono">3 crédits offerts • Aucune carte requise</motion.div>
          </motion.div>

          {/* Demo video card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-20 relative max-w-4xl"
          >
            <div className="glass rounded-2xl p-2">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
                <video
                  data-testid="hero-demo-video"
                  src="https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
                  className="w-full h-full object-cover"
                  autoPlay muted loop playsInline
                  preload="metadata"
                  poster={HERO_BG}
                  width="1280"
                  height="720"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center animate-pulse-glow">
                    <Play className="w-6 h-6 fill-white" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  Live demo
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}
          className="mb-16 max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Fonctionnalités</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">Tout ce qu'il faut pour créer des vidéos époustouflantes.</h2>
        </motion.div>
        <motion.div
          variants={stagger} initial="hidden" whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          <motion.div variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.25 }} className="md:col-span-8 md:row-span-2 card-dark p-8 relative overflow-hidden group cursor-default">
            <div className="absolute inset-0 opacity-40 group-hover:opacity-60 transition-opacity" style={{ backgroundImage: `url(${FEATURE_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D14] via-[#0D0D14]/60 to-transparent" />
            <div className="relative">
              <Video className="w-8 h-8 text-cyan-400 mb-6" />
              <h3 className="font-display text-3xl font-semibold mb-2">Génération vidéo par IA</h3>
              <p className="text-white/60 max-w-md">Modèles diffusion dernier cri pour des séquences fluides, détaillées et fidèles à votre vision.</p>
            </div>
          </motion.div>
          {[
            { i: Wand2, t: "Texte → Vidéo", d: "Décrivez votre scène, laissez l'IA la créer.", c: "text-indigo-400" },
            { i: ImageIcon, t: "Image → Vidéo", d: "Animez n'importe quelle photo en un clip vivant.", c: "text-cyan-400" },
            { i: Sparkles, t: "Qualité HD", d: "Résolution nette, prête à publier.", c: "text-indigo-400" },
            { i: Zap, t: "Génération rapide", d: "Vos vidéos en secondes, pas en heures.", c: "text-cyan-400" },
            { i: Download, t: "Téléchargement libre", d: "MP4 haute qualité, aucun watermark.", c: "text-indigo-400" },
          ].map((f) => (
            <motion.div key={f.t} variants={fadeUp} whileHover={{ y: -4 }} transition={{ duration: 0.25 }} className="md:col-span-4 card-dark p-6 cursor-default">
              <f.i className={`w-6 h-6 ${f.c} mb-4`} />
              <h3 className="font-display text-xl font-medium mb-1">{f.t}</h3>
              <p className="text-sm text-white/60">{f.d}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}
          className="mb-16 max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Fonctionnement</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">Trois étapes. C'est tout.</h2>
        </motion.div>
        <motion.div
          variants={stagger} initial="hidden" whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid md:grid-cols-3 gap-8"
        >
          {[
            { n: "01", i: PenLine, t: "Écrire un prompt", d: "Décrivez votre scène ou importez une image de référence." },
            { n: "02", i: Settings2, t: "Choisir les paramètres", d: "Format, qualité, durée. Tout est ajustable en un clic." },
            { n: "03", i: Download, t: "Générer & télécharger", d: "L'IA travaille, vous récupérez le fichier." },
          ].map((s) => (
            <motion.div key={s.n} variants={fadeUp} whileHover={{ y: -6 }} transition={{ duration: 0.3 }} className="card-dark p-8 relative group">
              <div className="text-xs font-mono text-white/30 mb-4">{s.n}</div>
              <s.i className="w-8 h-8 text-cyan-400 mb-4 transition-transform group-hover:scale-110 group-hover:rotate-3" />
              <h3 className="font-display text-2xl font-medium mb-2">{s.t}</h3>
              <p className="text-white/60 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7 }}
          className="card-dark p-12 md:p-20 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 hero-glow opacity-70" />
          <div className="relative">
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold mb-4">Prêt à créer ?</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">Rejoignez des milliers de créateurs qui donnent vie à leurs idées avec VisionAI Studio.</p>
            <Link to="/register" data-testid="cta-register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-indigo-600 hover:bg-indigo-500 btn-glow font-medium">
              Commencer gratuitement <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </section>

      </main>
      <Footer />
    </div>
  );
}
